import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GOLD, NAVY_FORM } from './colors';
import { LoadError, useIsNarrow } from './ui';
import { useDarkMode } from './DarkModeContext';

// OPSLAYOUT: the COO Operational Tasks presentation, shared rather than re-styled here.
import { OpsTabs, OpsTable, OpsDueCell, OpsDateCell } from './opsTaskLayout';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// ATTESTVIEW: the compliance officer's Attestations view, Submitted / Pending.
//
// THE INTERFACE PATTERN is the tabbed task list the employee and ceo shells already use
// (EmployeeView.js's Tasks section): a row of tab buttons, each carrying a count badge, with the
// selected tab's rows below it and an explicit empty message when a tab is empty. Gold badge for
// the actionable tab, green for the settled one, matching that screen's own convention. Filters
// are ADDED here (type, period, entity) because this list is a register of the whole population
// rather than one person's handful of tasks; the task pattern itself has none, so they follow the
// COO employee list's shape instead: client side over the one fetched payload.
//
// WHY A SEPARATE SCREEN RATHER THAN EXTENDING THE EXISTING ATTESTATIONS REGISTER. The register in
// ComplianceReport is a flat export-oriented table shared with the COO and admin, reached through
// its generic RegistersView, and it is driven by /registers/attestations. Adding tabs, counts and
// a click-through there would change a screen two other roles use, which this task must not do.
// This view reads its own endpoint and leaves the register untouched.
//
// WHAT IT DOES NOT HAVE: an approval control. An attestation has exactly two states, and
// completion is self-only by confirmed decision (ATTESTSELF), so there is no approval action to
// expose. The server states this in the detail payload (`approval_available: false`) rather than
// leaving the client to infer it.

const TYPE_LABELS = {
  pad: 'Personal Account Dealing',
  geh: 'Gifts and Entertainment',
  outside_affiliations: 'Outside Affiliations',
  public_officials: 'Public Officials',
};

const typeLabel = (t) => TYPE_LABELS[t] || (t || '-');

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = toDisplayDate(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function ComplianceAttestations({ onOpenAttestation }) {
  const { isDarkMode } = useDarkMode();
  const isNarrow = useIsNarrow();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [tab, setTab] = useState('pending');
  const [fType, setFType] = useState('');
  const [fPeriod, setFPeriod] = useState('');
  const [fEntity, setFEntity] = useState('');
  const [search, setSearch] = useState('');
  const [hoveredId, setHoveredId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    axios.get(`${API}/compliance/attestations/view`)
      .then(res => { setData(res.data || null); setLoading(false); })
      .catch(() => { setData(null); setLoadFailed(true); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const T = {
    card:    { backgroundColor: isDarkMode ? '#16233d' : '#fff', borderRadius: '10px', padding: '20px',
               border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#EEE'}`,
               boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)' },
    title:   { fontSize: '14px', fontWeight: 600, color: isDarkMode ? '#e8eaf0' : NAVY_FORM, margin: '0 0 14px 0' },
    // OPSLAYOUT: the period sits inside the Attestation cell beside the type, muted so the
    // type stays the primary value, which is how the COO view treats a task's own detail.
    periodNote: { color: isDarkMode ? '#9ba8c0' : '#888', fontWeight: 400 },
    nameBtn: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
               fontWeight: 600, fontSize: '13px', color: isDarkMode ? '#e8eaf0' : NAVY_FORM,
               textDecoration: 'underline' },
    empty:   { color: isDarkMode ? '#8b95a8' : '#667085', fontStyle: 'italic', fontSize: '13px', margin: 0 },
    select:  { padding: '7px 10px', fontSize: '12px', borderRadius: '6px',
               border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.3)' : '#ddd'}`,
               backgroundColor: isDarkMode ? '#16233d' : '#fff',
               color: isDarkMode ? '#e8eaf0' : '#666', cursor: 'pointer' },
  };

  const filters = data?.filters || { types: [], periods: [], entities: [] };
  const rowsFor = (which) => {
    let list = (which === 'submitted' ? data?.submitted : data?.pending) || [];
    if (fType) list = list.filter(r => r.attestation_type === fType);
    if (fPeriod) list = list.filter(r => r.period === fPeriod);
    if (fEntity) list = list.filter(r => r.entity === fEntity);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(r => (r.employee_name || '').toLowerCase().includes(q));
    return list;
  };

  // The tab badges count the SAME filtered sets the tables below render, so a badge can never
  // disagree with the rows behind it once a filter is applied.
  const submittedRows = rowsFor('submitted');
  const pendingRows = rowsFor('pending');
  const shown = tab === 'submitted' ? submittedRows : pendingRows;

  if (loading) {
    return <div style={T.card}><p style={T.empty}>Loading...</p></div>;
  }
  if (loadFailed) {
    return (
      <div style={T.card}>
        <LoadError message="Could not load attestations." onRetry={load} />
      </div>
    );
  }

  return (
    <div style={T.card}>
      <h3 style={T.title}>Attestations</h3>

      {/* OPSLAYOUT: the COO Operational Tasks tabs. PENDING IS FIRST AND THE DEFAULT, like
          Overdue on the COO view, because it is the tab that requires action. Counts render even
          at zero, so choosing one tab never hides the existence of the other. Navy pill when
          active, grey outlined pill when not; no green and no gold. */}
      <OpsTabs
        tabs={[
          { id: 'pending', label: 'Pending', count: pendingRows.length },
          { id: 'submitted', label: 'Submitted', count: submittedRows.length },
        ]}
        active={tab}
        onSelect={setTab}
        isDarkMode={isDarkMode}
      />

      {/* FILTERS - type, period and entity, over the one fetched payload, KEPT exactly as built.
          The options come from the SERVER's scoped rows, so a scoped officer is never offered a
          value their scope would return nothing for. */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap',
                    alignItems: 'center' }}>
        <input type="text" placeholder="Search by name..." aria-label="Search by name"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...T.select, cursor: 'text', minWidth: '170px' }} />
        <select aria-label="Filter by attestation type" value={fType}
          onChange={e => setFType(e.target.value)} style={T.select}>
          <option value="">All types</option>
          {filters.types.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
        <select aria-label="Filter by period" value={fPeriod}
          onChange={e => setFPeriod(e.target.value)} style={T.select}>
          <option value="">All periods</option>
          {filters.periods.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select aria-label="Filter by entity" value={fEntity}
          onChange={e => setFEntity(e.target.value)} style={T.select}>
          <option value="">All entities</option>
          {filters.entities.map(en => <option key={en} value={en}>{en}</option>)}
        </select>
      </div>

      {/* OPSLAYOUT: the COO column shape, Attestation / Employee / Due / Due date.
          Attestation carries the TYPE and the PERIOD together, as the brief specifies.
          The Due column differs by tab: days for Pending, the submission date for Submitted.
          Entity is kept as an extra column on a wide screen, since the filter offers it. */}
      <OpsTable
        columns={[
          { key: 'attestation', label: 'Attestation', strong: true },
          { key: 'employee', label: 'Employee' },
          ...(isNarrow ? [] : [{ key: 'entity', label: 'Entity' }]),
          { key: 'due', label: tab === 'submitted' ? 'Submitted on' : 'Due' },
          { key: 'due_date', label: 'Due date' },
        ]}
        rows={shown}
        rowKey={(r) => r.id}
        emptyText={tab === 'submitted' ? 'No submitted attestations' : 'No pending attestations'}
        isDarkMode={isDarkMode}
        cell={(r, key) => {
          if (key === 'attestation') {
            return (
              <span>
                {typeLabel(r.attestation_type)}
                {r.period ? <span style={T.periodNote}> {r.period}</span> : null}
              </span>
            );
          }
          if (key === 'employee') {
            // Clicking the NAME opens that employee's attestation, read only (ATTESTSELF).
            // A real button, so it is keyboard reachable like every other control in this shell.
            return (
              <button type="button" style={T.nameBtn}
                      onClick={() => onOpenAttestation && onOpenAttestation(r)}>
                {r.employee_name}
              </button>
            );
          }
          if (key === 'entity') return r.entity || '-';
          if (key === 'due') {
            // SUBMITTED: the date it was attested.
            if (tab === 'submitted') return <OpsDateCell value={r.attested_at} isDarkMode={isDarkMode} />;
            // PENDING: an attestation carries NO DUE DATE in the data model, and none is
            // invented. Verified: the Attestation table has no due/deadline column, and no
            // attestation Task exists to borrow one from (zero such rows). So the cell is left
            // EMPTY rather than guessing, exactly as the brief requires. The shared Due cell
            // renders "No due date" for a null, which is the COO view's own wording for the same
            // situation, so the two read alike. The day the model gains a real due date, passing
            // it here is the whole change.
            return <OpsDueCell dueDate={r.due_date || null} daysOverdue={r.days_overdue}
                               isDarkMode={isDarkMode} />;
          }
          if (key === 'due_date') return <OpsDateCell value={r.due_date} isDarkMode={isDarkMode} />;
          return null;
        }}
      />
    </div>
  );
}
